<template>
  <div
    :class="`add-module add-module--${theme}`"
    v-if="draftType"
  >
    <slot
      name="trigger-button"
      :toggleMenu="toggleMenu"
    >
      <button
        v-if="theme !== 'panel'"
        ref="addModuleButton"
        class="comet-button comet-button--primary comet-button--icon-only add-module__button"
        :class="{
          'comet-button--fab': theme === 'panel',
        }"
        :disabled="disabled"
        :aria-expanded="menuVisible.toString()"
        :aria-label="$t('Add a Module')"
        @click="toggleMenu"
      >
        <NebulaIcon
          class-override="add-module__add-icon comet-button__icon comet-button__icon--left"
          symbol-id="add"
        />
      </button>
    </slot>
    <EditorMenu
      v-if="menuVisible || theme === 'panel'"
      :module-id="moduleId"
      :scale="scale"
      :trigger-element="$el"
      :trigger-element-id-horizontal="['placeholder', 'replace'].includes(theme)
        ? `mini-floating-menu-${moduleId}` : undefined"
      :update-menu-position-delay="['placeholder', 'replace'].includes(theme) ? 100 : 0"
      :is-floating="theme !== 'panel'"
      :contain-menu-vertical="theme !== 'notes'"
      :contain-menu-horizontal="canvasName !== 'notes'"
      :positions="editorMenuPositions"
      :box-name="theme === 'notes' || canvasName === 'notes' ? 'notes' : 'editor'"
      :append-to-editor="['placeholder', 'replace'].includes(theme)"
      id-prefix="add-module-floating-menu"
    >
      <transition
        name="add-module__list-transition"
        enter-from-class="add-module__list-transition--enter-from"
        enter-active-class="add-module__list-transition--enter-active"
        leave-active-class="add-module__list-transition--leave-active"
        leave-to-class="add-module__list-transition--leave-to"
      >
        <FocusTrap
          :active="menuVisible && !modalIsOpen"
          click-outside-deactivates
          :initial-focus="() => $refs.contentToggle || $refs.searchDEButton"
          :fallback-focus="() => $refs.contentToggle || $refs.searchDEButton"
          @deactivate="menuVisible = false"
        >
          <div
            ref="addModuleDropdowns"
            class="add-module__dropdown"
          >
            <h3
              v-if="theme !== 'panel'"
              class="add-module__heading"
            >
              {{ $t('Select to Add') }}
            </h3>
            <button
              v-if="quizItemModules.length"
              ref="contentToggle"
              class="add-module__accordion-toggle"
              :class="openAccordions.content && 'add-module__accordion-toggle--open'"
              :aria-expanded="openAccordions.content.toString()"
              @click="toggleAccordion('content')"
            >
              {{ $t('Content') }}
              <NebulaIcon symbol-id="caret-right" />
            </button>
            <transition
              :duration="menuTrasitionDuration"
              name="add-module__accordion-transition"
              enter-from-class="add-module__accordion-transition--enter-from"
              enter-active-class="add-module__accordion-transition--enter-active"
              leave-active-class="add-module__accordion-transition--leave-active"
              leave-to-class="add-module__accordion-transition--leave-to"
            >
              <div
                v-show="openAccordions.content"
                class="add-module__accordion"
              >
                <ul class="add-module__list">
                  <li
                    class="add-module-draggable add-module__list-item add-module__list-item--module"
                    v-if="canPaste"
                  >
                    <button
                      :aria-describedby="`add-module__paste-description--${uniqueId}`"
                      class="add-module-draggable__button"
                      @click="onPaste()"
                    >
                      {{ $t('Paste from Clipboard') }}
                      <span
                        :id="`add-module__paste-description--${uniqueId}`"
                        class="add-module__aria-description"
                      >
                        {{ $t('Press Enter to add a module') }}
                      </span>
                    </button>
                  </li>
                  <li class="add-module__list-item add-module__list-item--search">
                    <!-- eslint-disable max-len -->
                    <button
                      ref="searchDEButton"
                      :aria-describedby="`add-module__search-description--${uniqueId}`"
                      @click="onSearchClick ? onSearchClick() : openSearchAssetsModal()"
                    >
                      {{ $t('Search DE') }}
                      <span
                        :id="`add-module__search-description--${uniqueId}`"
                        class="add-module__aria-description"
                      >
                        {{ $t('Press Enter to search for an asset from the Discovery Education library') }}
                      </span>
                    </button>
                    <!-- eslint-enable max-len -->
                  </li>
                  <li
                    v-if="showMyContent"
                    class="add-module__list-item add-module__list-item--quicklist"
                  >
                    <button
                      :aria-describedby="`add-module__my-content-description--${uniqueId}`"
                      @click="onMyContentClick ? onMyContentClick() : openMyContentModal()"
                    >
                      {{ $t('From My Content') }}
                      <span
                        :id="`add-module__my-content-description--${uniqueId}`"
                        class="add-module__aria-description"
                      >
                        {{ $t('Press Enter to add an asset from My Content') }}
                      </span>
                    </button>
                  </li>
                  <li class="add-module__list-item add-module__list-item--quicklist">
                    <button
                      :aria-describedby="`add-module__quicklist-description--${uniqueId}`"
                      @click="onQuicklistClick ? onQuicklistClick() : openQuicklistModal()"
                    >
                      {{ $t('From Favorites') }}
                      <span
                        :id="`add-module__quicklist-description--${uniqueId}`"
                        class="add-module__aria-description"
                      >
                        {{ $t('Press Enter to add an asset from your Favorites') }}
                      </span>
                    </button>
                  </li>
                  <!--
                    This gets a callback function instead of an event listener, since it opens an
                    upload modal. The AddModule component might disappear when the modal opens, so
                    it can't listen to an event. A callback function can still be called, though.
                  -->
                  <UploadModuleDraggable
                    class="add-module__list-item add-module__list-item--module
                      add-module__list-item--upload"
                    :class="`add-module__list-item--${canvasName}`"
                    ref="uploadDraggable"
                    :allow-multiple="uploadAllowMultiple !== undefined
                      ? uploadAllowMultiple
                      : !moduleToReplace"
                    :canvas-name="canvasName"
                    :module-to-replace="moduleToReplace"
                    :options="uploadModuleOptions"
                    :upload-callback="uploadCallback || addModulesFromMyContent"
                  />
                  <AddModuleDraggable
                    v-for="mod in contentModules"
                    :key="getContentModuleKey(mod)"
                    :options="mod"
                    class="add-module__list-item add-module__list-item--module"
                    :class="`add-module__list-item--${canvasName}`"
                    ref="addModuleDraggable"
                    @addModule="placeModule(mod)"
                  />
                </ul>
              </div>
            </transition>

            <template v-if="quizItemModules.length">
              <button
                class="add-module__accordion-toggle"
                :class="openAccordions.quizItems && 'add-module__accordion-toggle--open'"
                :aria-expanded="openAccordions.quizItems.toString()"
                @click="toggleAccordion('quizItems')"
              >
                {{ $t('Quiz Items') }}
                <NebulaIcon symbol-id="caret-right" />
              </button>
              <transition
                :duration="menuTrasitionDuration"
                name="add-module__accordion-transition"
                enter-from-class="add-module__accordion-transition--enter-from"
                enter-active-class="add-module__accordion-transition--enter-active"
                leave-active-class="add-module__accordion-transition--leave-active"
                leave-to-class="add-module__accordion-transition--leave-to"
              >
                <div
                  v-show="openAccordions.quizItems"
                  class="add-module__accordion"
                >
                  <ul class="add-module__list">
                    <AddModuleDraggable
                      v-for="mod in quizItemModules"
                      :key="mod.type"
                      :options="mod"
                      class="add-module__list-item add-module__list-item--module"
                      :class="`add-module__list-item--${canvasName}`"
                      ref="addModuleDraggable"
                      @click="placeModule(mod)"
                    />
                  </ul>
                </div>
              </transition>
            </template>
          </div>
        </FocusTrap>
      </transition>
    </EditorMenu>
  </div>
</template>

<script>
import { NebulaIcon } from '@discoveryedu/nebula-components';
import { FocusTrap } from 'focus-trap-vue';
import interact from 'interactjs';
import { get, uniqueId } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import * as types from '@/lib/constants/store';
import { bus } from '@/lib/eventBus';
import { scanGrid } from '@/lib/utils/grid';
import {
  EDITOR_CANVAS_HEIGHT,
  EDITOR_CANVAS_WIDTH,
  MODAL_CANVAS_WIDTH,
  EDITOR_CANVAS_SLIDES_MAX_ROWS,
} from '@/lib/constants';
import {
  textModuleDefault,
  wordTrackingModuleDefault,
  advancedButtonModuleDefault,
  placeholderModuleDefault,
  uploadModuleDefault,
  promptModuleDefault,
  singleSelectionModuleDefault,
  multipleSelectionModuleDefault,
  blockModuleDefault,
} from '@/lib/constants/moduleDefaults';
import AddModuleDraggable from './AddModuleDraggable.vue';
import UploadModuleDraggable from './UploadModuleDraggable.vue';
import EditorMenu from '@/components/EditorMenu.vue';
import addModule from '@/mixins/addModule';
import {
  useEditorStore,
  useModalStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'AddModule',
  components: {
    AddModuleDraggable,
    FocusTrap,
    NebulaIcon,
    UploadModuleDraggable,
    EditorMenu,
  },
  mixins: [addModule],
  inject: {
    getCanvasRefs: {
      default() {
        return () => {};
      },
    },
  },
  props: {
    allowDrag: {
      type: Boolean,
      default: true,
    },
    canvasName: {
      type: String,
      required: true,
    },
    moduleToReplace: {
      type: Object,
      default: null,
    },
    theme: {
      type: String,
      default: 'panel', // out of ['panel', 'notes', 'placeholder', 'replace']
    },
    scale: {
      type: Number,
      default: 1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    onQuicklistClick: {
      type: Function,
      default: null,
    },
    onSearchClick: {
      type: Function,
      default: null,
    },
    onMyContentClick: {
      type: Function,
      default: null,
    },
    uploadCallback: {
      type: Function,
      default: null,
    },
    uploadAllowMultiple: {
      type: Boolean,
      default: undefined,
    },
    moduleId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      openAccordions: {
        content: true,
        quizItems: this.theme === 'panel',
      },
      menuVisible: false,
      menuTrasitionDuration: 250,
      defaultModW: 1,
      defaultModH: 2,
      uniqueId: uniqueId(),
    };
  },
  watch: {
    menuVisible() {
      this.$nextTick(this.initializeDraggables);
    },
    theme() {
      this.$nextTick(this.initializeDraggables);
    },
    canvasName() {
      this.$nextTick(this.initializeDraggables);
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'draftType',
      'getModulesByModalIdOrderedByZPosition',
      'modulesOrderedByZPosition',
      'isModuleClipboardEmpty',
      'currentModalCanvasId',
      'region',
      'moduleClipboard',
      'editorSnapGrid',
    ]),
    ...mapState(useModalStore, [
      'modalIsOpen',
    ]),
    ...mapState(useUserStore, [
      'userIsStudent',
      'isDeUser',
    ]),
    editorMenuPositions() {
      if (['placeholder', 'replace'].includes(this.theme)) {
        return this.canvasName === 'notes' ? ['right'] : ['left', 'right'];
      }
      return ['top'];
    },
    themeName() {
      return (get(this, 'currentPage.options.theme.name'));
    },
    snapToGrid() {
      return this.editorSnapGrid && this.gridIsOff;
    },
    contentModules() {
      if (this.theme === 'replace') {
        return [];
      }

      const modules = [
        textModuleDefault(this.gridIsOff, this.themeName),
      ];

      if (!this.userIsStudent && this.canvasName !== 'notes') {
        modules.push(wordTrackingModuleDefault(this.gridIsOff, this.themeName));
      }

      modules.push(advancedButtonModuleDefault(this.gridIsOff, this.themeName));

      if (this.isDeUser && this.theme !== 'placeholder') {
        modules.push(placeholderModuleDefault(this.gridIsOff));
      }

      return modules;
    },
    modulesOrderedByZ() {
      if (this.canvasName === 'modalModules') {
        return this.getModulesByModalIdOrderedByZPosition(this.currentModalCanvasId);
      }

      return this.modulesOrderedByZPosition;
    },
    quizItemModules() {
      const editorStore = useEditorStore();
      // Quiz items cannot be added by students, inside the notes or modal canvases,
      // or as an option for replacing media
      if (this.canvasName === 'notes'
        || this.canvasName === 'modalModules'
        || this.userIsStudent
        || this.theme === 'replace'
        || editorStore.isEditingRestricted) return [];

      return [
        promptModuleDefault(this.gridIsOff),
        singleSelectionModuleDefault(this.gridIsOff),
        multipleSelectionModuleDefault(this.gridIsOff),
        blockModuleDefault(this.gridIsOff),
      ];
    },
    showMyContent() {
      return !this.isDeUser;
    },
    uploadModuleOptions() {
      return uploadModuleDefault(this.gridIsOff);
    },
    canPaste() {
      return !this.isModuleClipboardEmpty && (!this.moduleToReplace || this.theme === 'placeholder');
    },
  },
  created() {
    bus.on('editor-canvas-carousel:start-page-change-animation', this.onPageNavigationStart);
    bus.on('editor-canvas-carousel:start-touch-move-animation', this.onPageNavigationStart);
  },
  mounted() {
    this.initializeDraggables();
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_CURRENTLY_EDITING,
      types.PASTE_MODULE_FROM_CLIPBOARD,
    ]),
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
      types.SET_MY_CONTENT_MODAL_OPTIONS,
    ]),
    getContentModuleKey(mod) {
      const isWordTracking = get(mod, 'options.word_tracking.enabled', false);
      return isWordTracking
        ? `${mod.type}-word-tracking`
        : mod.type;
    },
    getCanvasComponent() {
      return this.getCanvasRefs()[this.canvasName];
    },
    toggleAccordion(accordion) {
      this.openAccordions[accordion] = !this.openAccordions[accordion];

      if (this.theme !== 'panel') {
        // For floating menus outside the Add panel, opening one accordion
        // should collapse the others
        Object.keys(this.openAccordions)
          .filter((key) => key !== accordion)
          .forEach((key) => {
            this.openAccordions[key] = false;
          });
      }
    },
    toggleMenu() {
      if (this.menuVisible === false) {
        this.menuVisible = true;
      }
    },
    openMyContentModal() {
      this[types.SET_MY_CONTENT_MODAL_OPTIONS]({
        config: {
          hideDistrictTab: this.region === 'UK', // see DEX-2646
          isActive: true,
          isStudent: this.userIsStudent,
          selectMultiple: !this.moduleToReplace,
        },
        onChooseAssets: this.addModulesFromMyContent,
      });
    },
    openSearchAssetsModal() {
      const vm = this;
      this[types.OPEN_MODAL]({
        type: 'SearchAssets',
        canvasName: this.canvasName,
        moduleToReplace: this.moduleToReplace,
        afterConfirm() {
          // After assets are chosen, the dropdown can be closed
          vm.menuVisible = false;
        },
      });
    },
    openQuicklistModal() {
      const vm = this;
      this[types.OPEN_MODAL]({
        type: 'FromQuicklist',
        canvasName: this.canvasName,
        moduleToReplace: this.moduleToReplace,
        afterConfirm() {
          // After assets are chosen, the dropdown can be closed
          vm.menuVisible = false;
        },
      });
    },
    initializeDraggables() {
      if (!this.allowDrag) return;

      const vm = this;
      let $draggingCopy = null;
      let nameOfCanvasEntered = null; // flag for drop area
      const moduleDraggables = [
        this.$refs.uploadDraggable,
        ...(this.$refs.addModuleDraggable || []), // addModule draggables
      ];

      // If the template isn't rendered yet, escape
      if (!moduleDraggables.every((ref) => ref)) return;

      // Track "entered" state of preview module
      bus.on('beginPreview', (data) => {
        nameOfCanvasEntered = data.canvasEntered;
      });

      let canvasComponent;

      // Attach drag functionality onto addModule draggables
      moduleDraggables.forEach((element) => interact(element.$el).draggable({
        inertia: true,
        listeners: {
          start(event) {
            canvasComponent = vm.getCanvasComponent();

            // Initialize module to be added later
            bus.emit('initPlaceholderModule', {
              canvasName: vm.canvasName,
              ...element.options,
            });

            // Create floating addModuleDraggable
            $draggingCopy = event.target.cloneNode(true);
            $draggingCopy.classList.add('add-module__list-item--dragging');
            vm.$root.$el.appendChild($draggingCopy);

            /*
              Set the default module height and width so it can be used
              when calculating the x/y position.
            */
            vm.defaultModW = element.options.layout.width;
            vm.defaultModH = element.options.layout.height;
          },
          move(event) {
            // Grab location of the destination canvas, and the Add Module list
            // (the dragging element's positioned ancestor)
            const canvas = canvasComponent?.$refs?.gridLayout?.$el;
            if (!canvas) return;

            const { x: canvasX, y: canvasY } = canvas.getBoundingClientRect();
            const { x: rootX, y: rootY } = vm.$root.$el.getBoundingClientRect();

            // Calculate where the dragging element should display:
            // the difference between the mouse position and its positioned ancestor
            const draggableX = event.clientX - rootX;
            const draggableY = event.clientY - rootY;

            // Set the dragging element's position
            $draggingCopy.style.top = `${draggableY}px`;
            $draggingCopy.style.left = `${draggableX}px`;

            // Calculate the position of the new module:
            // the difference between the mouse position and the canvas,
            // divided by the scale of the canvas (e.g. if the mouse is 100px away
            // from the edge of the canvas but the canvas is scaled up 200%, that's
            // really only 50px in terms of the original size of the canvas)
            const canvasTop = event.clientY - canvasY;
            const canvasLeft = event.clientX - canvasX;
            const { x, y } = vm.snapToGrid
              ? vm.calcXYForSnapGrid(canvasTop, canvasLeft)
              : vm.calcXY(canvasTop / vm.scale, canvasLeft / vm.scale);

            bus.emit('updateDraggableXY', { canvasName: vm.canvasName, x, y });
            if (nameOfCanvasEntered === vm.canvasName) {
              // If the module enters the drop area, emit an event
              // to the canvas to show position of preview module
              canvasComponent.$refs.gridLayout.eventBus.emit('dragEvent', 'dragmove', '00000000', x, y, vm.defaultModH, vm.defaultModW);
            }
          },
          end() {
            // Destroy the dragging element
            vm.$root.$el.removeChild($draggingCopy);
            $draggingCopy = null;
          },
        },
      }));
    },
    calcXYForSnapGrid(top, left) {
      const { cellWidth, cellHeight } = this.editorSnapGrid;
      const cellWidthScaled = cellWidth * this.scale;
      const cellHeightScaled = cellHeight * this.scale;
      const xCells = Math.round(left / cellWidthScaled);
      const yCells = Math.round(top / cellHeightScaled);
      return {
        x: xCells * cellWidth,
        y: yCells * cellHeight,
      };
    },
    calcXY(top, left) {
      const { gridLayout } = this.getCanvasComponent().$refs;
      const colWidth = this.calcColWidth();
      const slidesMaxRows = this.gridIsOff
        ? EDITOR_CANVAS_HEIGHT
        : EDITOR_CANVAS_SLIDES_MAX_ROWS;
      const maxRows = this.draftType === 'board' || this.canvasName === 'notes'
        ? Infinity : slidesMaxRows;

      // Magic math to find the destination row/column. The drop zone for each column is
      // the column itself plus half the margin on either side. The remaining half margin
      // on the left side is subtracted at the beginning, leaving us with:
      // (mousePosition - leadingHalfMargin) / (columnWidthWithHalfMargins)
      // Note that this places the center of a 1x1 grid item; larger items will expand
      // to the right/bottom rather than expanding from the center
      let x = Math.floor((left - gridLayout.margin[0] / 2)
        / (colWidth + gridLayout.margin[0]));
      let y = Math.floor((top - gridLayout.margin[1] / 2)
        / (gridLayout.rowHeight + gridLayout.margin[1]));

      // Prevent returning values that are off the canvas if using grid
      if (!this.gridIsOff) {
        x = Math.max(Math.min(x, gridLayout.colNum - this.defaultModW), 0);
        y = Math.max(Math.min(y, maxRows - this.defaultModH), 0);
      }

      return { x, y };
    },
    calcColWidth() {
      const { gridLayout } = this.getCanvasComponent().$refs;

      const containerWidth = this.currentModalCanvasId
        ? MODAL_CANVAS_WIDTH
        : EDITOR_CANVAS_WIDTH;

      // The width of one column is the total width minus all gutters,
      // divided by the number of columns. For example:
      // (1000px - 13 * gutterWidth) / 12
      return (containerWidth
              - (gridLayout.margin[0]
              * (gridLayout.colNum + 1))) / gridLayout.colNum;
    },
    async onPaste() {
      if (this.isModuleClipboardEmpty) return;

      this[types.PASTE_MODULE_FROM_CLIPBOARD]({
        module: this.moduleClipboard,
        canvasName: this.canvasName,
        moduleToReplace: this.moduleToReplace,
      });
    },
    onPageNavigationStart() {
      if (['replace', 'placeholder'].includes(this.theme) && this.menuVisible) {
        this.menuVisible = false;
      }
    },
    scanGrid,
  },
  beforeUnmount() {
    const moduleDraggables = [
      this.$refs.uploadDraggable,
      ...(this.$refs.addModuleDraggable || []), // addModule draggables
    ];
    moduleDraggables.forEach((element) => {
      if (element?.$el) interact(element.$el).unset();
    });
    bus.off('editor-canvas-carousel:start-page-change-animation', this.onPageNavigationStart);
    bus.off('editor-canvas-carousel:start-touch-move-animation', this.onPageNavigationStart);
  },
};
</script>

<style lang="stylus">
// Note: These styles are also used by the "add menu" inside the Block module.

.add-module {
  position: relative;

  &--placeholder { // .add-module--placeholder
    .add-module__button { // .add-module--placeholder .add-module__button
      background-color: $comet-color-neutral-50;
      height: 48px; // match FAB style
      padding: 12px; // match FAB style
      width: 48px; // match FAB style

      &:hover {
        background-color: $comet-color-neutral-30;
      }
    }

    .add-module__add-icon { // .add-module--placeholder .add-module__add-icon
      fill: $comet-background-color-light;
    }
  }

  &--panel { // .add-module--panel
    .add-module__dropdown {
      border-radius: 0;
      box-shadow: none;
      position: static;
    }
  }

  &--notes { // .add-module--notes
    width: 100%;

    .add-module__button { // .add-module--notes .add-module__button
      border-radius: 0;
      display: block;
      width: 100%;
    }
  }

  &__button { // .add-module__button
    align-items: center;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    margin: 0;
    transition: all $nebula-transition-default;
  }

  &__aria-description {
    display: none;
  }

  &__dropdown {
    background: $comet-background-color-default;
    border-radius: $nebula-border-radius-badge-default;
    box-shadow: $comet-shadow-z-shadow-3;
    min-width: 250px;
    white-space: nowrap;
  }

  &__heading {
    nebula-text-h5();

    border-bottom: $comet-border-hairline-on-light;
    border-radius: $nebula-border-radius-badge-default $nebula-border-radius-badge-default 0 0;
    margin: 0;
    padding: $nebula-space-1x $nebula-space-2x $nebula-space-1x $nebula-space-2x;
  }

  &__accordion-toggle {
    align-items: center;
    background: none;
    border: none;
    border-bottom: $comet-border-hairline-on-light;
    border-radius: 0;
    display: flex;
    font-weight: bold;
    justify-content: space-between;
    padding: $nebula-space-1x $nebula-space-2x $nebula-space-1x $nebula-space-2x;
    text-align: start;
    transition: $nebula-transition-default;
    width: 100%;

    &:hover, &:focus {
      background: $comet-color-neutral-95;
    }

    svg {
      fill: $comet-color-azure-40;
      height: $comet-size-icon-m;
      transform: rotate(0deg);
      transition: $nebula-transition-default;
      width: $comet-size-icon-m;
    }

    &--open svg {
      transform: rotate(90deg);
    }
  }

  &__accordion {
    max-height: 40px * 8; // allow space for eight items
    overflow: hidden;
  }

  &__list {
    margin: 0;
    padding: 0;
  }

  &__list-item {
    list-style-type: none;

    button {
      background: transparent;
      border: 0;
      border-radius: 0;
      padding: $nebula-space-1x $nebula-space-2x $nebula-space-1x $nebula-space-2x;
      text-align: start;
      width: 100%;
    }

    &--dragging {
      border: $comet-border-hairline-on-light;
      border-radius: $nebula-border-radius-badge-default;
      position: absolute;
      transform: translate(-50%, -50%);
      z-index: 500;
    }

    &--search,
    &--my-content,
    &--quicklist,
    &--module {
      border-bottom: $comet-border-hairline-on-light;
      cursor: pointer;
      transition: background $nebula-transition-default;

      &:hover {
        background: $comet-color-neutral-95;
      }
    }
  }

  html[dir="rtl"] & {
    &__button {
      margin: 0;
    }
  }
}

/* Vue transition classes */
.add-module__list-transition--enter-active {
  transition: all .2s ease;
}
.add-module__list-transition--leave-active {
  transition: all .2s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.add-module__list-transition--enter-from, .add-module__list-transition--leave-to {
  opacity: 0;
  transform: translate(-50%, -100px);
}

.add-module__accordion-transition {
  &--enter-active,
  &--leave-active {
    transition: max-height $nebula-transition-default;
  }
  &--enter-from, &--leave-to {
    max-height: 0;
  }
}

</style>
